import CardActions from '@/components/base/card/card-actions.component';
import Button from '@/components/base/button.component';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { CartSummaryCard } from '../cart-summary-card.component';
import { useGlobalStore } from '@/stores';
import { useOrderCart } from '@/api/orders/orders.hooks.ts';
import { useCart } from '@/api/carts/carts.hooks.ts';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { CartAlreadyOrderedDialog } from './cart-already-ordered.dialog';

export function CartConfirmSummaryCard() {
  const navigate = useNavigate();
  const { t } = useTranslation('cart');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const {
    addressName2,
    addressName3,
    street,
    zipCode,
    city,
    country,
    remarks,
    reference,
  } = useGlobalStore();
  const { data, isPending } = useCart();
  const { mutateAsync: orderCart, isPending: isOrdering } = useOrderCart();
  const cartItems = useMemo(() => data?.cartItems || [], [data]);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);

  const handleOrderCart = async () => {
    if (isOrdering) return;
    try {
      const { id } = await orderCart({
        shippingAddress: {
          addressName2,
          addressName3,
          street,
          zipCode,
          city,
          country,
        },
        remarks,
        reference,
      });

      navigate(`/bp/c/cart/4/${id}`);
    } catch (error) {
      // 409: the cart had already been ordered. The backend issued a fresh
      // cart with the items restored — refresh cart data, then explain the
      // situation so the user can place the order again.
      if (isAxiosError(error) && error.response?.status === 409) {
        await queryClient.invalidateQueries({ queryKey: ['cart'] });
        setConflictDialogOpen(true);
        return;
      }
      enqueueSnackbar(t('error.title'), { variant: 'error' });
    }
  };

  return (
    <>
      <CartSummaryCard>
        <CardActions divider={false}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/bp/c/cart/2')}
          >
            {t('confirm.backToAddress')}
          </Button>
          <Button
            endIcon={<ArrowForward />}
            onClick={handleOrderCart}
            variant={'contained'}
            disabled={isPending || cartItems.length === 0 || isOrdering}
          >
            {isPending || isOrdering
              ? t('actions.loading')
              : t('confirm.placeOrder')}
          </Button>
        </CardActions>
      </CartSummaryCard>
      <CartAlreadyOrderedDialog
        open={conflictDialogOpen}
        onClose={() => setConflictDialogOpen(false)}
      />
    </>
  );
}
